import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomArt = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    size: '',
    color_palette: '',
    theme: '',
    additional_notes: ''
  });
  const [loading, setLoading] = useState(false);

  const sizes = ['Small (12" x 16")', 'Medium (18" x 24")', 'Large (24" x 36")', 'Extra Large (36" x 48")', 'Custom Size'];
  const palettes = ['Ocean Blues', 'Forest Greens', 'Sunset Warm', 'Monochrome', 'Vibrant Mixed', 'Pastel Dreams'];
  const themes = ['Ocean Waves', 'Nature & Flora', 'Sports & Automotive', 'Abstract Motion', 'Geometric', 'Personalized'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please login to submit a custom art request');
      navigate('/login');
      return;
    }

    if (!formData.size || !formData.color_palette || !formData.theme) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/custom-art`, formData);
      toast.success('Custom art request submitted! We\'ll contact you soon.');
      navigate('/profile');
    } catch (error) {
      console.error('Failed to submit request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFAFA' }}>
      <div className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="custom-art-title"
            >
              Commission Custom Art
            </h1>
            <p className="text-[#4A4A4A] text-base max-w-2xl mx-auto leading-relaxed">
              Create something uniquely yours. Share your vision and we'll bring it to life with our signature resin artistry.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onSubmit={handleSubmit}
            className="bg-white p-8 md:p-12 rounded-lg border border-black/5"
            data-testid="custom-art-form"
          >
            {/* Size Selection */}
            <div className="mb-8">
              <label className="block text-sm uppercase tracking-widest mb-4">Select Size *</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sizes.map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => setFormData({ ...formData, size })}
                    data-testid={`size-option-${size}`}
                    className={`p-4 border rounded-lg text-left transition-all ${
                      formData.size === size
                        ? 'border-[#1A1A1A] bg-[#F5F5F0]'
                        : 'border-[#E5E5E5] hover:border-[#1A1A1A]'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Color Palette */}
            <div className="mb-8">
              <label className="block text-sm uppercase tracking-widest mb-4">Choose Color Palette *</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {palettes.map((palette) => (
                  <button
                    key={palette}
                    type="button"
                    onClick={() => setFormData({ ...formData, color_palette: palette })}
                    data-testid={`palette-option-${palette}`}
                    className={`p-4 border rounded-lg text-center transition-all ${
                      formData.color_palette === palette
                        ? 'border-[#1A1A1A] bg-[#F5F5F0]'
                        : 'border-[#E5E5E5] hover:border-[#1A1A1A]'
                    }`}
                  >
                    {palette}
                  </button>
                ))}
              </div>
            </div>

            {/* Theme Selection */}
            <div className="mb-8">
              <label className="block text-sm uppercase tracking-widest mb-4">Select Theme *</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {themes.map((theme) => (
                  <button
                    key={theme}
                    type="button"
                    onClick={() => setFormData({ ...formData, theme })}
                    data-testid={`theme-option-${theme}`}
                    className={`p-4 border rounded-lg text-left transition-all ${
                      formData.theme === theme
                        ? 'border-[#1A1A1A] bg-[#F5F5F0]'
                        : 'border-[#E5E5E5] hover:border-[#1A1A1A]'
                    }`}
                  >
                    {theme}
                  </button>
                ))}
              </div>
            </div>

            {/* Additional Notes */}
            <div className="mb-8">
              <label className="block text-sm uppercase tracking-widest mb-4" htmlFor="notes">
                Additional Notes (Optional)
              </label>
              <textarea
                id="notes"
                value={formData.additional_notes}
                onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                data-testid="custom-art-notes"
                rows={6}
                placeholder="Share any specific ideas, inspirations, or references for your custom artwork..."
                className="w-full px-4 py-3 border border-[#E5E5E5] rounded-lg focus:outline-none focus:border-[#1A1A1A] transition-colors resize-none"
              />
            </div>

            {/* Pricing Info */}
            <div className="mb-8 p-6 bg-[#E0E8E9] rounded-lg">
              <h3 className="text-sm uppercase tracking-widest mb-3">Pricing & Timeline</h3>
              <p className="text-[#4A4A4A] text-sm leading-relaxed mb-2">
                Custom artwork pricing starts at $499 and varies based on size and complexity.
              </p>
              <p className="text-[#4A4A4A] text-sm leading-relaxed">
                Timeline: 2-4 weeks from approval to completion.
              </p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              data-testid="custom-art-submit-btn"
              className="w-full bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-full px-8 py-6 text-sm uppercase tracking-widest transition-all duration-300 disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
            
            {!user && (
              <p className="text-center text-sm text-[#888888] mt-4">
                You need to <button onClick={() => navigate('/login')} className="text-[#1A1A1A] hover:underline">login</button> to submit a custom art request
              </p>
            )}
          </motion.form>
        </div>
      </div>
    </div>
  );
};

export default CustomArt;
